import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Rating from '@mui/material/Rating';
import Alert from '@mui/material/Alert';
import CssBaseline from '@mui/material/CssBaseline';
import SendIcon from '@mui/icons-material/Send';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HelpIcon from '@mui/icons-material/Help';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function FeedbackContent() {
  const [feedbackType, setFeedbackType] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [rating, setRating] = React.useState(0);
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement actual submission
    setSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFeedbackType('');
      setSubject('');
      setMessage('');
      setRating(0);
    }, 3000);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '800px' }, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
          Feedback
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Help us improve myGrow Collective by sharing your thoughts
        </Typography>
      </Stack>

      {/* Quick Actions */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
        <Card
          variant="outlined"
          sx={{
            p: 2,
            flex: 1,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'error.main', bgcolor: alpha('#ef4444', 0.05) },
          }}
          onClick={() => setFeedbackType('bug')}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BugReportIcon sx={{ color: 'error.main' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Report a Bug</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Something not working right?
              </Typography>
            </Box>
          </Stack>
        </Card>
        <Card
          variant="outlined"
          sx={{
            p: 2,
            flex: 1,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'warning.main', bgcolor: alpha('#f59e0b', 0.05) },
          }}
          onClick={() => setFeedbackType('feature')}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LightbulbIcon sx={{ color: 'warning.main' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Feature Request</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Have an idea for improvement?
              </Typography>
            </Box>
          </Stack>
        </Card>
        <Card
          variant="outlined"
          sx={{
            p: 2,
            flex: 1,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'info.main', bgcolor: alpha('#3b82f6', 0.05) },
          }}
          onClick={() => setFeedbackType('question')}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <HelpIcon sx={{ color: 'info.main' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Ask a Question</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Need help with something?
              </Typography>
            </Box>
          </Stack>
        </Card>
      </Stack>

      {/* Feedback Form */}
      <Card variant="outlined" sx={{ p: 3 }}>
        {submitted ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Thank you for your feedback! We'll review it shortly.
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                Send Feedback
              </Typography>

              <FormControl fullWidth size="small">
                <InputLabel>Feedback Type</InputLabel>
                <Select
                  value={feedbackType}
                  label="Feedback Type"
                  onChange={(e) => setFeedbackType(e.target.value)}
                  required
                >
                  <MenuItem value="bug">Bug Report</MenuItem>
                  <MenuItem value="feature">Feature Request</MenuItem>
                  <MenuItem value="question">Question</MenuItem>
                  <MenuItem value="general">General Feedback</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                fullWidth
                size="small"
                required
              />

              <TextField
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
                multiline
                rows={5}
                required
                placeholder="Please describe your feedback in detail..."
              />

              <Box>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  How would you rate your overall experience?
                </Typography>
                <Rating
                  value={rating}
                  onChange={(e, newValue) => setRating(newValue)}
                  size="large"
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                endIcon={<SendIcon />}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
              >
                Submit Feedback
              </Button>
            </Stack>
          </form>
        )}
      </Card>
    </Box>
  );
}

export default function FeedbackPage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <SideMenu />
            <AppNavbar />
            <Box
              component="main"
              sx={(theme) => ({
                flexGrow: 1,
                backgroundColor: theme.vars
                  ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                  : alpha(theme.palette.background.default, 1),
                overflow: 'auto',
                minHeight: '100vh',
              })}
            >
              <Stack
                sx={{
                  pt: { xs: 10, md: 3 },
                  pb: 5,
                }}
              >
                <FeedbackContent />
              </Stack>
            </Box>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
